import type { Maybe } from '@adornis/base/utilTypes.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { DateTime } from 'luxon';
import { Student } from '../../users/entities/Student.js';
import { type Partner, PartnerPerProductStatistics } from '../entities/Partner.js';

const hefteAggregation = (partnerID: string, intervall: SchoolYearIntervall) => [
  { $match: { status: 'abgeschlossen' } },
  { $project: { createdAt: 1, order: '$items', school: '$shippingDetails.shippingSchoolID' } },
  { $unwind: '$order' },
  {
    $match: {
      'order.productCategory': 'Lernmaterial',
      createdAt: { $gte: intervall.start, $lt: intervall.end },
      school: partnerID,
    },
  },
  { $group: { _id: null, sum: { $sum: '$order.quantity' } } },
  { $project: { count: '$sum', _id: 0 } },
];

export type SchoolYearIntervall = { start: Date; end: Maybe<Date> };

/**
 * Converts a Förderjahr string to an intervall again to check DateTime against.
 * @param schoolYear a string of the form `FJ 17/18`
 *
 * CAREFUL: despite the name this is NOT a school year. A Förderjahr runs from the 1. of May to the 30. of
 * April, a school year from the 1. of August to the 31. of July. Do not fold this into school-year.ts and do
 * not feed BuT year labels into it -- the two turning points are genuinely different.
 *
 * TODO this will break for the next century.
 */
export function getIntervalsForSchoolYearString(schoolYear: string): SchoolYearIntervall {
  const start = '20' + schoolYear.slice(3, 5);
  const end = '20' + schoolYear.slice(6, 8);

  return {
    start: DateTime.fromISO(`${start}-05-01T00:00:00+00:00`).toJSDate(),
    end: DateTime.fromISO(`${end}-04-30T21:00:00+00:00`).toJSDate(),
  };
}

function intervalsOverlap(interval1: SchoolYearIntervall, interval2: SchoolYearIntervall): boolean {
  // Check for basic overlap scenario (both ends defined)
  if (interval2.end && interval1.end) {
    return interval1.start < interval2.end && interval1.end > interval2.start;
  }

  // If interval2 end is undefined, consider it infinite
  return interval1.start < interval2.start;
}

export async function getUsageStatisticsForSchoolyear(schoolYear: string, partner: Partner) {
  const ProductCHF = (await import('../../../_cms/products/entities/ProductCHF.js')).ProductCHF;
  const KPF = (await import('../../kpf/entities/KPF.js')).KPF;
  const Order = (await import('../../../LogistikOrder.js')).LogistikOrder;
  const StudentParticipationRelationship = (await import('../../../semantics/StudentParticipationRelationship.js'))
    .StudentParticipationRelationship;

  const kpfsOfPartner = await loadMongoData(KPF, { schoolID: partner._id });
  const coll = await getRawCollection(Order._collectionName);

  const intervall = getIntervalsForSchoolYearString(schoolYear);
  const statistics: PartnerPerProductStatistics[] = [];

  const ragProducts = partner.ragProducts;
  const usedRagProductClasses: string[] = [];

  const hefteCount = (
    await coll.aggregate<{ count: number }>(hefteAggregation(partner._id, intervall)).toArray()
  )[0] as undefined | { count: number };
  statistics.push(
    new PartnerPerProductStatistics({
      _id: `${schoolYear}_${ProductCHF._class.slice(-3)}_${partner._id}`,
      usage: hefteCount?.count ? hefteCount.count : 0,
    }),
  );

  // fill in actual kpfs
  for (const kpf of kpfsOfPartner) {
    const matchingRAGProduct = ragProducts
      ?.find(p => p._class === kpf.produkt._class)
      ?.ragYearInformation?.find(rag => rag.year === schoolYear);

    const matchingRAGProductUsage =
      matchingRAGProduct?.numberOfStudents !== undefined && matchingRAGProduct?.numberOfStudents !== null
        ? parseInt(matchingRAGProduct.numberOfStudents)
        : 0;

    // extra bread for chancenhefte. Not number of students but number of orders
    if (kpf.produkt._class === ProductCHF._class) {
      continue;
    }

    const studentRelationsshipsOfKPF = await loadMongoData(StudentParticipationRelationship, { objectID: kpf._id });
    const studentsOfKPF = await loadMongoData(Student, {
      _id: { $in: studentRelationsshipsOfKPF.map(rel => rel.subjectID ?? '') },
    });

    const studentsActiveInYear = studentsOfKPF.filter(student => {
      const studentStart = student.joinInformation?.eintrittsdatum;
      if (!studentStart) return false;

      const studentEnd = student.kuendigung?.austrittsdatum;

      return intervalsOverlap(intervall, { start: studentStart, end: studentEnd });
    });

    statistics.push(
      new PartnerPerProductStatistics({
        _id: schoolYear + '_' + kpf.produkt._class.slice(-3) + '_' + partner._id,
        usage: studentsActiveInYear.length,
        ragManualUsage: matchingRAGProductUsage,
      }),
    );
    usedRagProductClasses.push(kpf.produkt._class);
  }

  // if a ragProduct is on its own -> fill in the rest
  for (const ragProduct of ragProducts?.filter(p => !usedRagProductClasses.includes(p._class)) ?? []) {
    const matchingRAGProduct = ragProduct?.ragYearInformation?.find(rag => rag.year === schoolYear);
    const matchingRAGProductUsage =
      matchingRAGProduct?.numberOfStudents !== undefined && matchingRAGProduct?.numberOfStudents !== null
        ? parseInt(matchingRAGProduct.numberOfStudents)
        : 0;

    // search for the premade statistic and update it not create a new one for Hefte
    if (ragProduct._class === ProductCHF._class) {
      const alreadyMadeStatistic = statistics.find(
        s => s._id === schoolYear + '_' + ProductCHF._class.slice(-3) + '_' + partner._id,
      );
      if (alreadyMadeStatistic) {
        alreadyMadeStatistic.ragManualUsage = matchingRAGProductUsage;
      }
    } else {
      statistics.push(
        new PartnerPerProductStatistics({
          _id: schoolYear + '_' + ragProduct._class.slice(-3) + '_' + partner._id,
          usage: 0,
          ragManualUsage: matchingRAGProductUsage,
        }),
      );
    }
  }

  return statistics;
}
